// src/lib/holeSchematic.js
// Pure geometry for the per-hole schematic preview. Turns course_coordinates
// POI points into projected + rotated drawing geometry (tee at bottom, green at
// top). No SVG/React here — kept separate so the projection math is testable.
// Renders entirely from our own coordinate data; no map basemap involved.

import { POI, LOC, distanceYards, greenPoints } from './gps.js'

const M_PER_DEG_LAT = 111320
const toRad = (d) => (d * Math.PI) / 180

// Classify a POI code into a drawable kind + style hint + short label.
function classify(poi) {
  switch (poi) {
    case POI.GREEN: return null // greens handled separately (ellipse)
    case POI.GREEN_BUNKER:
    case POI.FAIRWAY_BUNKER: return { kind: 'bunker', label: '' }
    case POI.WATER: return { kind: 'water', label: '' }
    case POI.TREES: return { kind: 'trees', label: '' }
    case POI.MARK_100: return { kind: 'marker', label: '100' }
    case POI.MARK_150: return { kind: 'marker', label: '150' }
    case POI.MARK_200: return { kind: 'marker', label: '200' }
    case POI.DOGLEG: return { kind: 'dogleg', label: '' }
    case POI.FRONT_TEE:
    case POI.BACK_TEE: return null // tees handled separately
    default: return { kind: 'other', label: '' }
  }
}

// Equirectangular projection to a local meter plane (math coords, +y = north).
function projector(lat0, lng0) {
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(toRad(lat0))
  return (lat, lng) => ({
    x: (lng - lng0) * mPerDegLng,
    y: (lat - lat0) * M_PER_DEG_LAT,
  })
}

// Rotate a point about the origin so a chosen vector ends up pointing +y (up).
function rotator(angle) {
  const c = Math.cos(angle)
  const s = Math.sin(angle)
  return (p) => ({ x: p.x * c - p.y * s, y: p.x * s + p.y * c })
}

// Build drawing geometry for one hole. Returns null if there's nothing usable.
// PAD is in meters; the SVG viewBox is in meter units (real-world aspect kept).
export function buildHoleGeometry(coords, hole, { pad = 18 } = {}) {
  const rows = (coords || [])
    .filter((c) => Number(c.hole) === Number(hole) && c.latitude != null && c.longitude != null)
    .map((c) => ({
      poi: Number(c.poi),
      location: c.location == null ? null : Number(c.location),
      side: c.side_fw ?? null,
      lat: Number(c.latitude),
      lng: Number(c.longitude),
    }))
  if (rows.length === 0) return null

  // Projection origin = centroid of all points for this hole.
  const lat0 = rows.reduce((s, r) => s + r.lat, 0) / rows.length
  const lng0 = rows.reduce((s, r) => s + r.lng, 0) / rows.length
  const project = projector(lat0, lng0)

  const teeRow = rows.find((r) => r.poi === POI.BACK_TEE) || rows.find((r) => r.poi === POI.FRONT_TEE)
  const greens = greenPoints(coords, hole)
  const greenMid = greens.middle

  // Orientation: rotate so tee→green points up. Fall back to north-up.
  let angle = 0
  if (teeRow && greenMid) {
    const t = project(teeRow.lat, teeRow.lng)
    const g = project(greenMid.lat, greenMid.lng)
    const phi = Math.atan2(g.y - t.y, g.x - t.x)
    angle = Math.PI / 2 - phi // align the tee→green vector with +y
  }
  const rotate = rotator(angle)
  const pr = (lat, lng) => rotate(project(lat, lng))

  // Collect everything in rotated meter space, tracking the bounding box.
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  const track = (p) => {
    if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x
    if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y
    return p
  }

  const tee = teeRow ? track(pr(teeRow.lat, teeRow.lng)) : null
  const green = {
    front: greens.front ? track(pr(greens.front.lat, greens.front.lng)) : null,
    middle: greenMid ? track(pr(greenMid.lat, greenMid.lng)) : null,
    back: greens.back ? track(pr(greens.back.lat, greens.back.lng)) : null,
  }
  const dogleg = (() => {
    const d = rows.find((r) => r.poi === POI.DOGLEG)
    return d ? track(pr(d.lat, d.lng)) : null
  })()

  const hazards = []
  const markers = []
  for (const r of rows) {
    const cls = classify(r.poi)
    if (!cls) continue
    const p = track(pr(r.lat, r.lng))
    if (cls.kind === 'marker') markers.push({ ...p, label: cls.label })
    else if (cls.kind === 'dogleg') { /* tracked above */ }
    else hazards.push({ ...p, kind: cls.kind, side: r.side })
  }

  if (!Number.isFinite(minX)) return null

  // Shift to a padded, positive coordinate space and flip Y for SVG (y down).
  const w = maxX - minX + pad * 2
  const h = maxY - minY + pad * 2
  const tx = (p) => (p == null ? null : { x: p.x - minX + pad, y: maxY - p.y + pad, label: p.label, kind: p.kind, side: p.side })

  // Green ellipse: oriented along front→back, sized by the front-back distance.
  let greenShape = null
  const gf = tx(green.front), gm = tx(green.middle), gb = tx(green.back)
  if (gm) {
    if (gf && gb) {
      const len = Math.hypot(gb.x - gf.x, gb.y - gf.y)
      const ang = Math.atan2(gb.y - gf.y, gb.x - gf.x) // SVG-space angle, degrees below
      greenShape = {
        cx: (gf.x + gb.x) / 2, cy: (gf.y + gb.y) / 2,
        ry: Math.max(len / 2, 6), rx: Math.max(len * 0.32, 5),
        rotateDeg: (ang * 180) / Math.PI - 90,
      }
    } else {
      greenShape = { cx: gm.x, cy: gm.y, ry: 9, rx: 7, rotateDeg: 0 }
    }
  }

  // Centerline: tee → dogleg (if any) → green center.
  const teeS = tx(tee)
  const dogS = tx(dogleg)
  const centerline = [teeS, dogS, gm].filter(Boolean)

  // Hole yardage = tee → green center (real-world, from raw lat/lng).
  const yards = (teeRow && greenMid)
    ? distanceYards(teeRow.lat, teeRow.lng, greenMid.lat, greenMid.lng)
    : null

  return {
    hole: Number(hole),
    viewBox: { w, h },
    tee: teeS,
    green: greenShape,
    greenCenter: gm,
    centerline,
    hazards: hazards.map(tx),
    markers: markers.map(tx),
    yards,
  }
}
